/**
 * The four UI languages of delapresse.fr (marketing site and console).
 *
 * French is the hand-written source; English is the translation source for
 * Spanish and Arabic (scripts/i18n/translate.mjs). Anything that only exists
 * in fr/en (database columns like name_fr / name_en, resource articles)
 * falls back to English for es and ar readers.
 */
import { useTranslation } from "react-i18next";

export const LOCALES = ["fr", "en", "es", "ar"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "fr";

/** Normalise any language tag ("en-US", "ar-SA", "es") to a supported locale. */
export function toLocale(value?: string | null): Locale {
  if (!value) return DEFAULT_LOCALE;
  const base = value.toLowerCase().split(/[-_]/)[0];
  return (LOCALES as readonly string[]).includes(base) ? (base as Locale) : DEFAULT_LOCALE;
}

/** BCP-47 tag used for Intl formatting and the <html lang> attribute. */
export const INTL_LOCALE: Record<Locale, string> = {
  fr: "fr-FR",
  en: "en-GB",
  es: "es-ES",
  ar: "ar",
};

export const LOCALE_LABEL: Record<Locale, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
  ar: "العربية",
};

export function isRtl(locale: Locale): boolean {
  return locale === "ar";
}

export function intlLocale(locale: Locale): string {
  return INTL_LOCALE[locale];
}

/** Pick a localised value from a partial map, falling back to en then fr. */
export function pickLocalized<T>(map: Partial<Record<Locale, T | null | undefined>>, locale: Locale): T | undefined {
  return map[locale] ?? map.en ?? map.fr ?? undefined;
}

/** Suffix-based DB columns (name_fr, name_en). Only fr/en exist in the schema. */
export function dbLocaleSuffix(locale: Locale): "fr" | "en" {
  return locale === "fr" ? "fr" : "en";
}

/** Current UI locale, normalised to the four supported values. */
export function useLocale(): Locale {
  const { i18n } = useTranslation();
  return toLocale(i18n.language);
}

/** Apply lang + dir on <html>. Called on init and on every language change. */
export function applyDocumentLocale(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = INTL_LOCALE[locale];
  document.documentElement.dir = isRtl(locale) ? "rtl" : "ltr";
}

/** Localised DB column (title_fr / title_en / title_es / title_ar) with en then fr fallback. */
export function pickDbText(row: object, base: string, locale: Locale): string {
  const rec = row as Record<string, unknown>;
  const get = (l: string) => {
    const v = rec[`${base}_${l}`];
    return typeof v === "string" && v.trim() ? v : "";
  };
  return get(locale) || get("en") || get("fr");
}
